import { useState } from "react";
import { createPortal } from "react-dom";
import {
  Mic,
  MicOff,
  Volume2,
  VolumeOff,
  PhoneOff,
  Stadium,
  X,
} from "lucide-react";
import { useProxVoice } from "../../contexts/ProxVoiceContext";
import { RiskSignalCards } from "../narrative/RiskSignalCards";
import { BrandIcon } from "../icon-bank/BrandIcon";
import { ProfileRail } from "../profile/ProfileRail";
import { publicProfileDraftFromProxVoice } from "../../lib/public-profile-store";
import type { ProxVoiceProfile } from "../../lib/services";

type TabId = "audio" | "audience" | "signals";

interface ProxVoicePanelProps {
  open: boolean;
  onClose: () => void;
  anchorRect: DOMRect | null;
}

const tabs: Array<{ id: TabId; label: string }> = [
  { id: "audio", label: "Audio" },
  { id: "audience", label: "Audience" },
  { id: "signals", label: "Signals" },
];

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function ProxVoicePanel({
  open,
  onClose,
  anchorRect,
}: ProxVoicePanelProps) {
  const voice = useProxVoice();
  const [tab, setTab] = useState<TabId>("audio");
  const [profile, setProfile] = useState<ProxVoiceProfile | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const [drag, setDrag] = useState<{
    x: number;
    y: number;
    top: number;
    right: number;
  } | null>(null);
  if (!open || !anchorRect) return null;

  const top = Math.min(anchorRect.bottom + 8, window.innerHeight - 520);
  const right = Math.max(12, window.innerWidth - anchorRect.right);
  const panelPosition = position ?? { top, right };

  return createPortal(
    <>
      <div
        className="fixed z-[9998] w-[360px] overflow-hidden rounded-xl fintheon-fade-in"
        style={panelPosition}
        onMouseMove={(event) => {
          if (!drag) return;
          setPosition({
            top: Math.max(12, drag.top + event.clientY - drag.y),
            right: Math.max(12, drag.right - (event.clientX - drag.x)),
          });
        }}
        onMouseUp={() => setDrag(null)}
        onMouseLeave={() => setDrag(null)}
      >
        <div
          className="overflow-hidden rounded-xl"
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--fintheon-surface) 85%, transparent), color-mix(in srgb, var(--fintheon-bg) 92%, transparent))",
            backdropFilter: "blur(20px) saturate(1.3)",
            WebkitBackdropFilter: "blur(20px) saturate(1.3)",
            border:
              "1px solid color-mix(in srgb, var(--fintheon-accent) 18%, transparent)",
            boxShadow:
              "0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 color-mix(in srgb, var(--fintheon-accent) 8%, transparent)",
          }}
        >
          <div
            className="flex cursor-move items-center justify-between px-4 py-3"
            style={{
              borderBottom:
                "1px solid color-mix(in srgb, var(--fintheon-accent) 12%, transparent)",
            }}
            onMouseDown={(event) =>
              setDrag({
                x: event.clientX,
                y: event.clientY,
                top: panelPosition.top,
                right: panelPosition.right,
              })
            }
          >
            <div className="flex items-center gap-2 text-[var(--fintheon-accent)]">
              <Stadium className="h-3.5 w-3.5" />
              <span className="text-[12px] font-semibold uppercase tracking-[0.16em]">
                Forum Voice
              </span>
            </div>
            <button
              onClick={onClose}
              onMouseDown={(event) => event.stopPropagation()}
              className="ml-auto rounded-md p-1 text-[var(--fintheon-muted)] transition-colors hover:bg-white/5 hover:text-[var(--fintheon-accent)]"
              title="Close Forum voice"
              aria-label="Close Forum voice"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div
            className="flex gap-0.5 px-2 py-1.5"
            style={{
              borderBottom:
                "1px solid color-mix(in srgb, var(--fintheon-accent) 8%, transparent)",
            }}
          >
            {tabs.map((item) => (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                className="flex-1 rounded-lg px-2 py-1.5 text-[10px] uppercase tracking-wide transition-all duration-200"
                style={{
                  color:
                    tab === item.id
                      ? "var(--fintheon-accent)"
                      : "var(--fintheon-muted)",
                  background: "transparent",
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="max-h-[420px] min-h-[200px] overflow-y-auto p-3 custom-scrollbar">
            {tab === "audio" && (
              <div className="space-y-3">
                <div className="rounded-lg border border-[var(--fintheon-accent)]/15 p-3">
                  <p className="text-xs text-[var(--fintheon-text)]/60">
                    {voice.state === "connected"
                      ? "Live on the Fintheon floor."
                      : voice.state === "connecting"
                        ? "Connecting to the floor."
                        : "Voice is idle."}
                  </p>
                  {voice.error && (
                    <p className="mt-2 text-xs text-red-300">{voice.error}</p>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => void voice.toggleMute()}
                    className="fintheon-action-link rounded-lg border border-[var(--fintheon-accent)]/15 px-3 py-2 text-xs"
                  >
                    {voice.muted ? (
                      <MicOff className="mx-auto h-4 w-4" />
                    ) : (
                      <Mic className="mx-auto h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={voice.toggleDeafen}
                    className="fintheon-action-link rounded-lg border border-[var(--fintheon-accent)]/15 px-3 py-2 text-xs"
                  >
                    {voice.deafened ? (
                      <VolumeOff className="mx-auto h-4 w-4" />
                    ) : (
                      <Volume2 className="mx-auto h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={voice.disconnect}
                    className="fintheon-action-link rounded-lg border border-[var(--fintheon-accent)]/15 px-3 py-2 text-xs text-red-300"
                  >
                    <PhoneOff className="mx-auto h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            {tab === "audience" && (
              <div className="grid grid-cols-4 gap-3">
                {voice.participants.map((participant) => (
                  <button
                    key={participant.identity}
                    onClick={() =>
                      participant.profile && setProfile(participant.profile)
                    }
                    className={`proxvoice-pill-shimmer flex flex-col items-center gap-1 rounded-md bg-[var(--fintheon-bg)] p-2 transition-all duration-300 hover:opacity-80 ${
                      participant.leaving
                        ? "opacity-0"
                        : "fintheon-fade-in opacity-100"
                    } ${
                      participant.isSpeaking ||
                      (participant.audioLevel ?? 0) > 0.08
                        ? "proxvoice-speaking-card"
                        : ""
                    }`}
                    style={{
                      border:
                        "1px solid color-mix(in srgb, var(--fintheon-accent) 14%, transparent)",
                    }}
                  >
                    {participant.profile?.avatarUrl ? (
                      <img
                        src={participant.profile.avatarUrl}
                        alt=""
                        className={`h-10 w-10 rounded-full object-cover ${participant.isSpeaking ? "proxvoice-speaking-ring" : ""}`}
                      />
                    ) : (
                      <span
                        className={`profile-avatar-placeholder h-10 w-10 ${participant.isSpeaking ? "proxvoice-speaking-ring" : ""}`}
                        data-avatar-color={
                          participant.profile?.avatarColor ?? "orange"
                        }
                        aria-label={initials(participant.name) || "FT"}
                      />
                    )}
                    <span className="max-w-full truncate text-[10px] text-[var(--fintheon-text)]/65">
                      {participant.name}
                    </span>
                    <span className="max-w-full truncate text-[9px] text-[var(--fintheon-text)]/35">
                      {participant.profile?.position || "Desk Team"}
                    </span>
                    <span className="inline-flex max-w-full items-center gap-1 truncate rounded-sm bg-[var(--fintheon-surface)] px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.1em] text-[var(--fintheon-accent)]/70">
                      <BrandIcon
                        name={participant.profile?.broker || "Broker TBD"}
                        className="h-2.5 w-2.5"
                        fallback
                      />
                      {participant.profile?.broker || "Broker TBD"}
                    </span>
                    <span className="text-[8px] uppercase tracking-[0.1em] text-[var(--fintheon-text)]/30">
                      {participant.isMuted
                        ? "Muted"
                        : participant.outputMuted
                          ? "Speaker off"
                          : "Listening"}
                    </span>
                  </button>
                ))}
                {voice.participants.length === 0 && (
                  <p className="col-span-4 text-xs text-[var(--fintheon-text)]/45">
                    Nobody on the floor yet.
                  </p>
                )}
              </div>
            )}
            {tab === "signals" && <RiskSignalCards compact />}
          </div>
        </div>
      </div>
      {profile ? (
        <ProfileRail
          open
          profile={publicProfileDraftFromProxVoice(profile)}
          onClose={() => setProfile(null)}
        />
      ) : null}
    </>,
    document.body,
  );
}
